import React from "react";
import { Skeleton, Stack } from "@mantine/core";
import type { MantineSpacing } from "@mantine/core";

export interface SettingsListSkeletonProps {
  /** Number of placeholder bars. Defaults to 3. */
  rows?: number;
  /** Height of each bar in px. Defaults to 56. */
  rowHeight?: number;
  /** Space between bars. Defaults to "sm". */
  gap?: MantineSpacing;
  /** Accessible name announced while loading. */
  ariaLabel?: string;
  /** Test hook for the loading state. */
  "data-testid"?: string;
}

/**
 * Generic loading placeholder for a settings page body: a stack of rounded
 * bars. Deliberately content-agnostic (no table headers or card chrome), so it
 * reads correctly whether the page resolves to a list, a table, or an empty
 * state.
 */
export default function SettingsListSkeleton({
  rows = 3,
  rowHeight = 56,
  gap = "sm",
  ariaLabel = "Loading",
  "data-testid": testId,
}: SettingsListSkeletonProps) {
  return (
    <Stack
      gap={gap}
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} height={rowHeight} radius="md" />
      ))}
    </Stack>
  );
}
